import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Dialog } from "@mui/material";
import { Plus as PlusIcon } from "lucide-react";
import * as React from "react";

import { AlertDialog, PageLayout } from "@/shared/components";
import { AddToCollection } from "@/shared/components/collections";
import {
  DatasetsList as SharedDatasetsList,
  type DatasetRow,
} from "@/shared/components/datasets/DatasetsList";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type SelectionOption } from "@/shared/components/ResourceView";
import { useDeleteDatasets } from "@/shared/domain/datasets/hooks";
import { ButtonLink } from "@/shared/foundation/primitives/RobotoButton";
import { usePopupState } from "@/shared/hooks/usePopupState";
import { Goto } from "@/shared/navigation/Goto";

export function DatasetsList() {
  const { mutateAsync: deleteDatasets } = useDeleteDatasets();
  const showNotification = useNotification();

  const addToCollectionPopup = usePopupState<Set<string>>();
  const { open: openAddToCollectionPopup } = addToCollectionPopup;

  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [datasetsToDelete, setDatasetsToDelete] = React.useState<string[]>([]);

  const selectionOptions: SelectionOption<DatasetRow>[] = React.useMemo(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected }) => {
          openAddToCollectionPopup({
            metadata: new Set(selected.map((d) => d.dataset_id)),
          });
        },
      },
      {
        name: "Delete",
        icon: <DeleteOutlineIcon fontSize="small" />,
        action: ({ selected }) => {
          setDatasetsToDelete(selected.map((d) => d.dataset_id));
          setDeleteConfirmOpen(true);
        },
      },
    ],
    [openAddToCollectionPopup],
  );

  const handleDeleteConfirm = async () => {
    const results = await deleteDatasets(datasetsToDelete);
    const failures = results.filter((r) => r.status === "rejected").length;
    if (failures > 0) {
      showNotification(
        `${failures} dataset${failures !== 1 ? "s" : ""} could not be deleted`,
        "error",
      );
    }
  };

  return (
    <PageLayout title="Roboto - Datasets" headerContentLeft="Datasets">
      <SharedDatasetsList
        selectionOptions={selectionOptions}
        actions={
          <ButtonLink
            href={Goto.route.CreateDataset}
            variant="primary"
            icon={<PlusIcon aria-hidden="true" />}
          >
            New Dataset
          </ButtonLink>
        }
      />
      <Dialog
        open={addToCollectionPopup.isOpen}
        onClose={addToCollectionPopup.close}
      >
        <AddToCollection
          selected={{
            type: "Dataset",
            items: addToCollectionPopup.metadata ?? new Set(),
          }}
          onCancel={addToCollectionPopup.close}
        />
      </Dialog>
      <AlertDialog
        dialogOpen={deleteConfirmOpen}
        handleClose={() => {
          setDeleteConfirmOpen(false);
          setDatasetsToDelete([]);
        }}
        dialogTitle={`Delete ${datasetsToDelete.length} dataset${datasetsToDelete.length !== 1 ? "s" : ""}?`}
        dialogText="This action cannot be undone."
        dialogAction={handleDeleteConfirm}
      />
    </PageLayout>
  );
}
